import React, {
  useEffect,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from "react";
import CustomModalLayout from "../../../web/Auth/CustomModalLayout";
import { Input } from "../../../../shadcn/components/ui/input";
import { Dropdown } from "../../../../shadcn/components/custom/Dropdown";
import ScrollableDropdown from "../../../../shadcn/components/custom/dropdown/ScrollableDropdown";
import { Button } from "../../../../shadcn/components/ui/button";
import { BetaForm as useBetaForm } from "@reusejs/react";
import { useSelector, useDispatch } from "react-redux";

import {
  addEmail,
  addMobileNumber,
  createBidderOnboardingWithOutData,
  fetchIDCardDetails,
  getAllAssets,
  getAllCities,
  getAllStates,
  getAssetsbySlug,
  getBidderOnboardingOfUser,
  getCitiesbySlug,
  getStatesBySlug,
  updateBidderOnboarding,
} from "services/bidder";
import CheckIcon from "../../../../icons/CheckIcon";
import EditIcon from "../../../../icons/EditIcon";

import BidderOTPInputModal from "./BidderOTPInputModal";
import {
  setBidderEmail,
  setBidderMobile,
  setBidderOnboarding,
  setOnboarding,
} from "store";
import {
  aadhaarRegEx,
  altMobileNumberRegEx,
  emailRegEx,
  getBidderTypeLabel,
  getPanTypesByPan,
  gstRegex,
  mapDigioCategoryToBidderTypes,
  mobileNumberRegEx,
  nameRegex,
  ALL_BIDDER_TYPE_OPTIONS,
  normalizeBidderTypeForPanValidation,
  PanBidderType,
  panRegex,
  pinRegEx,
  updateUser,
  getPincodeDetails,
} from "services";
import axios from "axios";
import BidderInputMobileNumberFormModel from "./BidderInputMobileNumberFormModel";
import { ProfileCompletionCard } from "../../../../components/web/staticPages/dashboardPage/ProfileCompletionCard";
import { useProfileCompletionCardControl } from "../../../../components/web/staticPages/dashboardPage/hooks/useProfileCompletionCardControl";
import { useSmoothScroll } from "../../../../hoc";

const isDigioPanFullVerificationEnabled =
  process.env.NEXT_PUBLIC_DIGIO_PAN_FULL_VERIFICATION_ENABLED === "true";

const isPincodeBasedFetchingEnabled =
  process.env.NEXT_PUBLIC_ENABLE_PINCODE_BASED_FETCHING === "true";

interface BidderOnboardingFormPropsV2 {
  onOuterSubmitComplete?: () => void;
  excludeProfilePicture?: boolean;
}

type BidderType = {
  label: string;
  value: PanBidderType;
};

export interface BidderOnboardingFormPayload {
  uuid: string;
  user_uuid: string;
  user_can_update: boolean;
  email: string;
  mobile: string;
  type: string;
  name: string;
  authorized_person_name: string;
  alternate_mobile?: string;
  full_address: string;
  city: string;
  state: string;
  country: string;
  pin: string;
  pan: string;
  preferred_location?: string[];
  preferred_state?: string[];
  preferred_property_type?: string[];
  price_range?: string;
  aadhaar?: string;
  gst?: string;
  legal_entity_name?: string;
  picture?: string;
}

export default function BidderOnboardingFormV2({
  onOuterSubmitComplete = () => {},
  excludeProfilePicture = false,
}: BidderOnboardingFormPropsV2) {
  const dispatch = useDispatch();
  const bidderAuthUser = useSelector((state: any) => {
    return state.bidderAuth.user;
  });
  const authUser = useSelector((state: any) => {
    return state.auth.user;
  });

  const onboardingData = useSelector((state: any) => {
    return state.bidderAuth.onboarding;
  });

  const [loading, setLoading] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isNameVerified, setIsNameVerified] = useState(false);
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [inputModelOpen, setInputModalOpen] = useState(false);
  const [isEmail, setIsEmail] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isValidMobile, setIsValidMobile] = useState(false);
  const [userOnboarding, setUserOnboarding] = useState<any>({});
  const [isFetchingPan, setIsFetchingPan] = useState(false);
  const [panErrorCleared, setPanErrorCleared] = useState(false);
  const [mobileChangeState, setMobileChangeState] = useState({
    isConfirmingOpen: false,
    isConfirmingChange: false,
  });
  const [selectedBidderType, setSelectedBidderType] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const form = useBetaForm({
    uuid: "",
    user_uuid: "",
    user_can_update: true,
    email: "",
    mobile: "",
    type: "",
    name: "",
    authorized_person_name: "",
    alternate_mobile: "",
    full_address: "",
    city: "",
    state: "",
    country: "",
    pin: "",
    pan: "",
    preferred_location: [],
    preferred_state: [],
    preferred_property_type: [],
    price_range: "",
    aadhaar: "",
    gst: "",
    legal_entity_name: "",
  });

  // Common styling constants
  const dropdownContainerStyles = `border-[1px] border-brand-borderLightGray min-w-[130px] w-full rounded-none h-[50px] font-openSans font-normal text-sm leading-5 px-4`;
  const floatingLabelDropdownStyles = `absolute top-0.5 text-[10px] transition-all duration-300 left-3`;
  const errorStyles = `text-red-500 mt-1 text-sm`;
  const inputFieldStyles = `bg-white peer block w-full appearance-none border border-brand-borderLightGray pt-5 pb-2 pl-4 pr-4 h-[50px] focus:pb-2 outline-none font-openSans font-normal text-sm leading-5 text-brand-textLightGrey placeholder:text-gray-400 placeholder:text-sm`;
  const floatingLabelStyles = `absolute font-openSans font-normal left-4 transition-all duration-300 -translate-y-1/2 text-gray-400 top-3 text-[11px] leading-[12px]`;

  // Memoized data source functions to prevent recreation
  const getAssetsDataSource = useCallback(async (searchTerm = "") => {
    const assetsData: any = await getAllAssets(searchTerm);
    return assetsData.map((item: any) => ({
      label: item.name,
      value: item.slug,
      uuid: item.uuid,
    }));
  }, []);

  const getStatesDataSource = useCallback(async (searchTerm = "") => {
    const statesData: any = await getAllStates(searchTerm);
    return statesData.map((item: any) => ({
      value: item.slug,
      label: item.name,
      uuid: item.uuid,
    }));
  }, []);

  const getCitiesDataSource = useCallback(async (searchTerm = "") => {
    const citiesData: any = await getAllCities(searchTerm);
    return citiesData.map((item: any) => ({
      value: item.slug,
      label: item.name,
      uuid: item.uuid,
    }));
  }, []);

  const formConstraints = useMemo(() => {
    return {
      uuid: {
        presence: {
          allowEmpty: false,
        },
      },
      type: {
        presence: {
          allowEmpty: false,
        },
      },
      pan: {
        presence: {
          allowEmpty: false,
        },
        length: {
          maximum: 10,
        },
        format: {
          pattern: panRegex,
        },
      },
      name: {
        presence: {
          allowEmpty: false,
        },
        format: {
          pattern: nameRegex,
        },
      },
      email: {
        presence: {
          allowEmpty: false,
        },
        format: {
          pattern: emailRegEx,
          message: "^Email is not valid.",
        },
      },
      mobile: {
        presence: {
          allowEmpty: false,
        },
        format: {
          pattern: mobileNumberRegEx,
          message: "^Mobile number is not valid.",
        },
        length: {
          maximum: 10,
          message: "^Mobile number is too long (maximum is 10 characters)",
        },
      },
      alternate_mobile: {
        presence: false,
        format: {
          pattern: altMobileNumberRegEx,
          message: "^Mobile number is not valid.",
        },
        length: {
          maximum: 10,
          message: "^Mobile number is too long (maximum is 10 characters)",
        },
      },
      ...(form?.value?.type?.value === "individual"
        ? {
            aadhaar: {
              presence: { allowEmpty: false },
              format: {
                pattern: aadhaarRegEx,
                message: "Invalid Aadhaar number",
              },
            },
          }
        : {
            gst: {
              presence: { allowEmpty: false },
              format: { pattern: gstRegex, message: "Invalid GST number" },
            },
          }),
      pin: {
        presence: {
          allowEmpty: false,
        },
        length: {
          maximum: 6,
        },
        format: {
          pattern: pinRegEx,
        },
      },
      city: {
        presence: {
          allowEmpty: false,
        },
      },
      state: {
        presence: {
          allowEmpty: false,
        },
      },
      country: {
        presence: {
          allowEmpty: false,
        },
      },
      full_address: {
        presence: {
          allowEmpty: false,
        },
      },
      authorized_person_name: {
        presence: {
          allowEmpty: true,
        },
      },
      legal_entity_name: {
        presence: {
          allowEmpty: true,
        },
      },
      preferred_property_type: {
        presence: {
          allowEmpty: true,
        },
      },
      price_range: {
        presence: {
          allowEmpty: true,
        },
      },
      preferred_state: {
        presence: {
          allowEmpty: true,
        },
      },
      preferred_location: {
        presence: {
          allowEmpty: true,
        },
      },
    };
  }, [form?.value?.type]);

  const prefetchAndSaveInReduxOnboardingBidderData = useCallback(async () => {
    if (bidderAuthUser && bidderAuthUser?.uuid) {
      const existingOnboardingData: any = await getBidderOnboardingOfUser(
        bidderAuthUser.uuid,
      );

      if (existingOnboardingData) {
        setUserOnboarding(existingOnboardingData);
        dispatch(setOnboarding(onboardingData));
        dispatch(setBidderOnboarding(onboardingData));
      } else {
        const createInitialBidderOnboardingAccountAndSaveInRedux = async () => {
          const payload: any = {
            user_uuid: bidderAuthUser?.uuid,
          };
          if (bidderAuthUser?.profile?.name) {
            payload["name"] = bidderAuthUser.profile?.name;
          }
          const res: any = await createBidderOnboardingWithOutData(payload);

          if (res) {
            const onboardingFresh: any = await getBidderOnboardingOfUser(
              bidderAuthUser.uuid,
            );

            setUserOnboarding(onboardingFresh);
            dispatch(setOnboarding(onboardingFresh));
            dispatch(setBidderOnboarding(onboardingFresh));
          }
        };
        createInitialBidderOnboardingAccountAndSaveInRedux();
      }
    }
  }, [bidderAuthUser?.uuid]);

  useEffect(() => {
    prefetchAndSaveInReduxOnboardingBidderData();
  }, [prefetchAndSaveInReduxOnboardingBidderData]);

  useEffect(() => {
    const setValidationsOnMount = () => {
      if (userOnboarding && userOnboarding.uuid) {
        form.setValidationRules(formConstraints);
      }
    };
    setValidationsOnMount();
  }, [userOnboarding.uuid, formConstraints]);

  useEffect(() => {
    const prefillOnboardingForm = async () => {
      const emailAttribute = bidderAuthUser?.attributes?.find(
        (attr: any) => attr.type === "email",
      );
      if (emailAttribute) {
        form.setField("email", emailAttribute.value);
        setIsEmailVerified(true);
      }

      const mobileAttribute = bidderAuthUser?.attributes?.find(
        (attr: any) => attr.type === "mobile_number",
      );
      if (mobileAttribute) {
        const phonenumber = mobileAttribute.value.slice(3);
        form.setField("mobile", phonenumber);
        setIsMobileVerified(true);
      }

      if (bidderAuthUser?.uuid) {
        form.setField("user_uuid", bidderAuthUser?.uuid);
      }

      if (userOnboarding && userOnboarding.uuid) {
        form.setField("uuid", userOnboarding?.uuid);
        form.setField("user_can_update", userOnboarding?.user_can_update);
        const normalizedBidderType = normalizeBidderTypeForPanValidation(
          userOnboarding?.type,
        );
        const useEffectBidderTypeValue = userOnboarding?.type
          ? {
              label:
                getBidderTypeLabel(normalizedBidderType) ||
                userOnboarding?.type?.charAt(0)?.toUpperCase() +
                  userOnboarding?.type?.slice(1),
              value: normalizedBidderType || userOnboarding?.type,
            }
          : selectedBidderType;
        console.log(
          "useEffect type update is called*******",
          useEffectBidderTypeValue,
        );
        form.setField("type", useEffectBidderTypeValue);
        form.setField(
          "alternate_mobile",
          userOnboarding?.alternate_mobile
            ? userOnboarding?.alternate_mobile
            : "",
        );
        form.setField("pin", userOnboarding?.pin || "");
        form.setField("city", userOnboarding?.city || "");
        form.setField("state", userOnboarding?.state || "");
        form.setField("country", userOnboarding?.country || "");
        form.setField("full_address", userOnboarding?.full_address || "");
        form.setField("aadhaar", userOnboarding?.aadhaar || "");
        if (
          userOnboarding?.pan &&
          userOnboarding?.status === "active" &&
          userOnboarding?.uuid
        ) {
          form.removeError("pan");
          setPanErrorCleared(true);
          setTimeout(() => {
            form.setField("pan", userOnboarding?.pan);
            if (userOnboarding?.name) {
              form.setField("name", userOnboarding?.name);
              setIsNameVerified(true);
            }
          }, 200);
        }
        form.setField(
          "legal_entity_name",
          userOnboarding?.legal_entity_name
            ? userOnboarding?.legal_entity_name
            : "",
        );
        form.setField("gst", userOnboarding?.gst ? userOnboarding?.gst : "");
        form.setField(
          "price_range",
          userOnboarding?.price_range
            ? {
                label:
                  userOnboarding?.price_range.charAt(0).toUpperCase() +
                  userOnboarding?.price_range.slice(1),
                value: userOnboarding?.price_range,
              }
            : "",
        );
        const fetchLocations = async () => {
          const location_data: any = userOnboarding?.preferred_location;
          if (location_data) {
            const slugs = location_data?.map((city: any) => {
              return city;
            });
            const location_prefill: any = await getCitiesbySlug(slugs);
            const locations = location_prefill.map((city: any) => {
              return {
                label: city?.name,
                value: city?.slug,
                uuid: city?.uuid,
              };
            });
            form.setField("preferred_location", locations);
          }
        };
        const fetchStates = async () => {
          const location_data: any = userOnboarding?.preferred_state;
          if (location_data) {
            const slugs = location_data?.map((state: any) => {
              return state;
            });
            const location_prefill: any = await getStatesBySlug(slugs);
            const locations = location_prefill.map((state: any) => {
              return {
                label: state?.name,
                value: state?.slug,
                uuid: state?.uuid,
              };
            });
            form.setField("preferred_state", locations);
          }
        };
        const fetchPropertyType = async () => {
          const property_data: any = userOnboarding?.preferred_property_type;
          if (property_data) {
            const slugs = property_data.map((type: any) => {
              return type;
            });
            const property_prefill: any = await getAssetsbySlug(slugs);
            const property_type = property_prefill.map((asset: any) => {
              return {
                label: asset?.name,
                value: asset?.slug,
                uuid: asset?.uuid,
              };
            });
            form.setField("preferred_property_type", property_type);
          }
        };
        fetchLocations();
        fetchStates();
        fetchPropertyType();
      } else {
        form.setField("name", bidderAuthUser?.profile?.bio?.full_name || "");
      }
    };
    prefillOnboardingForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidderAuthUser, userOnboarding, selectedBidderType]);

  const baseTypes: BidderType[] = [
    { label: "Individual", value: "individual" },
  ];

  const types: BidderType[] =
    process?.env?.NEXT_PUBLIC_ENABLE_NI_ONBOARDING === "true"
      ? ALL_BIDDER_TYPE_OPTIONS
      : baseTypes;

  const priceRange = [
    { label: "1 to 10 Lakhs", value: "1 to 10 Lakhs" },
    { label: "10 to 50 Lakhs", value: "10 to 50 Lakhs" },
    { label: "50 Lakhs to 1 Cr", value: "50 Lakhs to 1 Cr" },
    { label: "1 Cr to 5 Cr", value: "1 Cr to 5 Cr" },
    { label: "5 Cr and Above", value: "5 Cr and Above" },
  ];

  const type = form.getField("type")?.value;
  const userCanUpdate = form.getField("user_can_update");

  const { isComplete, percentage, firstInCompleteSection } =
    useProfileCompletionCardControl({
      excludeProfilePicture: excludeProfilePicture,
      formValues: form?.value,
    });

  const { scrollToId } = useSmoothScroll();

  const handleSubmit = async () => {
    try {
      setLoading(true);

      setSubmitAttempted(true);
      const isValid = form.validate();

      if (!isValid) {
        setLoading(false);
        return;
      }

      if (!isMobileVerified) {
        form.pushError({ mobile: ["Mobile no. not verified"] });
        setLoading(false);
        return;
      }

      if (!isEmailVerified) {
        form.pushError({ email: ["Email id. not verified"] });
        setLoading(false);
        return;
      }

      form.startProcessing();
      let data: any = {};
      if (form.value?.uuid) {
        data = await updateBidderOnboarding(form.value);
      }

      const userProfileUpdatePayload = {
        uuid: authUser.uuid,
        ...authUser.profile,
        bio: {
          full_name: form.getField("name"),
        },
      };
      await updateUser(userProfileUpdatePayload);

      if (data?.uuid) {
        dispatch(setBidderOnboarding(data));
        dispatch(setOnboarding(data));
        onOuterSubmitComplete?.();
        form.setField("user_can_update", false);
        setLoading(false);
      }

      scrollToId("onboarding-form-top");
    } catch (error: any) {
      console.log("onboarding form v2 handleSubmit errors", error);
      if (error?.response?.data?.errors) {
        form.setErrors(error.response.data.errors);
      }
    } finally {
      form.finishProcessing();
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!submitAttempted) return;

    const errors = form.errors?.all?.();
    if (!errors) return;

    const keys = Object.keys(errors);
    if (keys.length === 0) return;

    scrollToId(keys[0], { focus: true });
  }, [scrollToId, submitAttempted, JSON.stringify(form.errors?.all?.())]);

  // PIN code validation and auto-fill address
  const handlePinCodeChange = async (val: string) => {
    console.log("val: ", val);
    form.setField("pin", val);
    if (val.length === 6) {
      if (isPincodeBasedFetchingEnabled) {
        // Feature flag ON: Use internal API via service
        getPincodeDetails(val)
          .then((pincodeData: any) => {
            form.setField("city", pincodeData.district);
            form.setField("state", pincodeData.state_name);
            form.setField(
              "country",
              pincodeData.country_ansi_code === "IN" ? "India" : "",
            );

            form.removeError("pin");
          })
          .catch((error: any) => {
            console.error("Error fetching pincode:", error?.response);
            form.setField("city", "");
            form.setField("state", "");
            form.setField("country", "");
            form.pushError({ pin: ["Enter Valid Pincode"] });
          });
      } else {
        // Feature flag OFF: Use external postalpincode.in API (original behavior)
        axios
          .get(`https://api.postalpincode.in/pincode/${val}`)
          .then((response) => {
            if (response.data[0].Status === "Success") {
              const place = response?.data[0]?.PostOffice;
              if (place) {
                const city = place[0]["District"];
                const state = place[0]["State"];
                const country = place[0]["Country"];
                form.setField("city", city);
                form.setField("state", state);
                form.setField("country", country);

                form.removeError("pin");
              } else {
                form.setField("city", "");
                form.setField("state", "");
                form.setField("country", "");
                form.pushError({ pin: ["Enter Valid Pin"] });
              }
            } else {
              form.setField("city", "");
              form.setField("state", "");
              form.setField("country", "");
              form.pushError({ pin: [response?.data[0]?.Message] });
            }
          })
          .catch((error) => {
            console.error("Error:", error?.response);
            form.setField("city", "");
            form.setField("state", "");
            form.setField("country", "");
          });
      }
    } else if (val.length > 6) {
      form.setField("city", "");
      form.setField("state", "");
      form.setField("country", "");
      form.pushError({ pin: ["PIN cannot exceed 6 digits"] });
    } else {
      form.setField("city", "");
      form.setField("state", "");
      form.setField("country", "");
    }
  };

  // Email and mobile verification handlers
  const handleSubmitVerify = async (type: any) => {
    try {
      if (type === "mobile") {
        const data: any = await addMobileNumber({
          value: form.getField("mobile"),
        });
        if (data) {
          dispatch(setBidderMobile(form.getField("mobile")));
          setIsEmail(false);
          setInputModalOpen(true);
        }
      } else if (type === "email") {
        if (emailRegEx.test(form.getField("email"))) {
          const data: any = await addEmail({
            value: form.getField("email"),
          });

          if (data) {
            dispatch(setBidderEmail(form.getField("email")));
            setIsEmail(true);
            setInputModalOpen(true);
          }
        } else {
          console.log("pppp");
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.value;

      // Handle "Value should be unique" error
      if (errorMessage[0] === "Value should be unique") {
        if (type === "email") {
          error.response.data.errors.value =
            "This email is already linked to another account";
        } else if (type === "mobile") {
          error.response.data.errors.value =
            "This mobile is already linked to another account";
        }
      }

      // Push appropriate error to the form
      if (error?.response?.data?.errors) {
        const finalMessage = error.response.data.errors.value;
        if (type === "email") {
          form.pushError({ email: [finalMessage] });
        } else if (type === "mobile") {
          form.pushError({ mobile: [finalMessage] });
        }
      }
    }
  };

  // PAN verification and name extraction
  const handleVerifyPan = async (panData: any) => {
    if (!panData) {
      // Called when PAN is cleared or type is changed - just clear verification state
      form.setField("pan", "");
      form.pushError({ pan: [] }); // Don't show error, just clear it
      setIsNameVerified(false);
      return;
    }

    const userEnteredName = (form.getField("name") || "").toString().trim();

    // Check if PAN status is valid first (independent of name)
    const isPanStatusValid = isDigioPanFullVerificationEnabled
      ? panData?.status?.toLowerCase() === "valid"
      : panData?.status === "VALID";

    // Only check name_as_per_pan_match if name has at least 3 characters
    // This prevents showing name mismatch error when name is incomplete
    const isNameLongEnough = userEnteredName.length >= 3;
    const isNameMatch = isDigioPanFullVerificationEnabled
      ? isNameLongEnough && panData?.name_as_per_pan_match === true
      : true; // Legacy doesn't have name_as_per_pan_match

    const selectedType = normalizeBidderTypeForPanValidation(
      form.getField("type")?.value,
    );

    // V4 mode: category is lowercase, pass as-is
    // Legacy mode: category is uppercase, pass as-is
    const categoryToMap = isDigioPanFullVerificationEnabled
      ? panData?.category?.toLowerCase()
      : panData?.category;

    const digioMatchedTypes = mapDigioCategoryToBidderTypes(categoryToMap);
    const isMatchedByDigioCategory =
      !!selectedType && digioMatchedTypes.includes(selectedType);

    if (!isMatchedByDigioCategory) {
      form.pushError({
        pan: ["Please enter valid PAN based on bidder type"],
      });
      // Legacy mode: clear name from API response
      // V4 mode: keep user's entered name (full_name not returned in V4)
      if (!isDigioPanFullVerificationEnabled) {
        form.setField("name", "");
      }
      setIsNameVerified(false);
    } else if (!isPanStatusValid) {
      // PAN status is invalid
      form.pushError({
        pan: ["PAN seems invalid. Please check the number and try again."],
      });
      if (!isDigioPanFullVerificationEnabled) {
        form.setField("name", "");
      }
      setIsNameVerified(false);
    } else if (!isNameMatch) {
      // PAN is valid but name doesn't match
      // Only show this error if name has enough characters
      if (!isNameLongEnough) {
        form.pushError({
          pan: [
            "Please enter your name (minimum 3 characters) before PAN verification.",
          ],
        });
      } else {
        form.pushError({
          pan: [
            "The name doesn't match your PAN records. Please enter your name exactly as on your PAN card.",
          ],
        });
      }
      if (!isDigioPanFullVerificationEnabled) {
        form.setField("name", "");
      }
      setIsNameVerified(false);
    } else {
      form.pushError({ pan: [] });

      // V4 mode: Don't overwrite user's entered name (full_name not returned)
      // Legacy mode: Use full_name from API response
      if (isDigioPanFullVerificationEnabled) {
        // V4: Keep user's entered name, just mark as verified
      } else {
        // Legacy: Use full_name from API
        form.setField("name", panData?.full_name);
      }
      setIsNameVerified(true);
    }
  };

  return (
    <>
      <ProfileCompletionCard
        isComplete={isComplete}
        percentage={percentage}
        firstInCompleteSection={firstInCompleteSection}
      />
      <section className="mt-7 mb-7 w-full space-y-5">
        <FormSection id="onboarding-type-pan" title="Type & PAN">
          <div className="w-full relative" id="type">
            <Dropdown
              disabled={!userCanUpdate}
              dataSource={() => {
                return Promise.resolve(types);
              }}
              defaultSelected={
                form.getField("type") !== "" ? form.getField("type") : null
              }
              selectIdentifier={true}
              onChange={async (value: any) => {
                form.setField("type", value);
                setSelectedBidderType(value);
                await handleVerifyPan(null);
              }}
              valueKey="value"
              placeholder="Select Bidder Type"
              styles={{
                hideDot: true,
                dropdownContentClass: "max-h-[320px] overflow-y-auto",
                dropdownItemsClass:
                  "text-brand-borderGrey whitespace-normal break-words text-left",
                hoverBgColor: "focus:bg-brand-lightPink",
                hoverTextColor: "focus:text-brand-borderGrey",
                outerContainer: `${dropdownContainerStyles} ${
                  !userCanUpdate
                    ? "bg-gray-50 cursor-not-allowed"
                    : "bg-white cursor-pointer"
                } ${
                  form.getField("type")?.value
                    ? !userCanUpdate
                      ? "items-end text-gray-600"
                      : "items-end text-brand-textLightGrey"
                    : !userCanUpdate
                    ? "text-gray-500"
                    : "text-brand-borderGrey"
                } ${!form.getField("type")?.value ? "pt-3" : ""}`,
              }}
            />
            <div
              className={`${floatingLabelDropdownStyles} ${
                form.getField("type")?.value
                  ? !userCanUpdate
                    ? "text-gray-500"
                    : "text-brand-borderGrey"
                  : !userCanUpdate
                  ? "text-gray-400"
                  : "text-gray-400"
              }`}
            >
              Bidder Type*
            </div>
            {form.errors.get("type") && (
              <p className={errorStyles}>{form.errors.get("type")[0]}</p>
            )}
          </div>

          {/* Name Field - Shown first when full verification is enabled */}
          {isDigioPanFullVerificationEnabled && (
            <FloatingLabelInput
              id="name-type-pan"
              placeholder="Enter your name"
              value={form.getField("name")}
              onChange={(e: any) => {
                form.setField("name", e?.target?.value?.toUpperCase());
                // Clear PAN when name changes (user needs to re-verify)
                if (!isNameVerified) {
                  form.setField("pan", "");
                  setPanErrorCleared(true);
                }
              }}
              disabled={!userCanUpdate || isNameVerified}
              label="Name*"
              error={form.errors.get("name")?.[0]}
              showCheckIcon={isNameVerified}
              inputFieldStyles={inputFieldStyles}
              floatingLabelStyles={floatingLabelStyles}
              errorStyles={errorStyles}
            />
          )}

          <div className={`relative w-full`} id="pan">
            <Input
              id="pan"
              placeholder={
                isDigioPanFullVerificationEnabled
                  ? form.getField("name")?.length < 3
                    ? "Enter name first"
                    : "Enter pan"
                  : form.getField("type")?.value !== "individual"
                  ? "Enter pan as per user type"
                  : "Enter pan"
              }
              disabled={
                !userCanUpdate ||
                (isDigioPanFullVerificationEnabled &&
                  form.getField("name")?.length < 3)
              }
              value={form.getField("pan")}
              onChange={async (e: any) => {
                if (e?.target?.value?.length < 11) {
                  let value = e?.target?.value.toUpperCase();
                  form.setField("pan", value);
                  setPanErrorCleared(false); // Reset error cleared state when user types

                  // If PAN doesn't match basic format (5 letters + 4 numbers + 1 letter)
                  if (!panRegex.test(value)) {
                    // Only show error when PAN is exactly 10 characters
                    if (value.length === 10) {
                      form.pushError({
                        pan: [
                          "Please enter a valid 10-character PAN number (e.g., ENBPM4556D)",
                        ],
                      });
                    } else {
                      form.pushError({ pan: [] }); // Clear any previous error
                    }
                    setIsFetchingPan(false);
                    setIsNameVerified(false);
                    return;
                  }

                  // PAN format is valid (5 letters + 4 numbers + 1 letter)
                  const selectedType = normalizeBidderTypeForPanValidation(
                    form.getField("type")?.value,
                  );
                  const panMatchedTypes = getPanTypesByPan(value);
                  const isMatchedByPanPrefix =
                    !!selectedType && panMatchedTypes.includes(selectedType);

                  if (!isMatchedByPanPrefix) {
                    form.pushError({
                      pan: [
                        "PAN type does not match selected bidder type. Please check bidder type.",
                      ],
                    });
                    setIsNameVerified(false);
                    setIsFetchingPan(false);
                    return;
                  }

                  try {
                    setIsFetchingPan(true);

                    // Full verification: require name >= 3 chars
                    const currentName = form.getField("name");

                    if (
                      isDigioPanFullVerificationEnabled &&
                      (currentName?.length || 0) < 3
                    ) {
                      form.pushError({
                        pan: [
                          "Please enter your name first (minimum 3 characters)",
                        ],
                      });
                      setIsFetchingPan(false);
                      return;
                    }

                    const payload: any = {
                      card_type: "PAN",
                      card_number: value,
                    };

                    // Add name if full verification is enabled AND name has at least 3 chars
                    // Otherwise, don't send name to allow legacy behavior
                    if (
                      isDigioPanFullVerificationEnabled &&
                      currentName?.length >= 3
                    ) {
                      payload.name = currentName?.toUpperCase() || "";
                    }

                    const res: any = await fetchIDCardDetails(payload);
                    setIsFetchingPan(false);
                    await handleVerifyPan(res);
                  } catch (error: any) {
                    // Handle 422 errors with Digio error messages
                    if (error?.response?.status === 422) {
                      const errorMessage =
                        error?.response?.data?.message?.message ||
                        "Validation failed. Please check your details.";
                      form.pushError({
                        pan: [errorMessage],
                      });
                    } else {
                      form.pushError({
                        pan: [
                          "An internal error occurred. Please try again later.",
                        ],
                      });
                    }
                    setIsFetchingPan(false);
                    setIsNameVerified(false);
                  }
                }
              }}
              className={inputFieldStyles}
            />
            <label htmlFor="pan" className={floatingLabelStyles}>
              Pan no.
            </label>
            {form.errors.get("pan") && !panErrorCleared && (
              <p className={errorStyles}>{form.errors.get("pan")[0]}</p>
            )}
          </div>
        </FormSection>

        <FormSection id="onboarding-basic-details" title="Basic Details">
          {!isDigioPanFullVerificationEnabled && (
            <FloatingLabelInput
              id="name"
              placeholder="Name"
              value={form.getField("name")}
              onChange={(e: any) => form.setField("name", e?.target?.value)}
              disabled={true}
              label="Name*"
              error={form.errors.get("name")?.[0]}
              showCheckIcon={isNameVerified}
              inputFieldStyles={inputFieldStyles}
              floatingLabelStyles={floatingLabelStyles}
              errorStyles={errorStyles}
            />
          )}

          <div className={`relative w-full`}>
            {isEmailVerified && (
              <div className="absolute top-1/2 right-3 z-10 -translate-y-1/2">
                <CheckIcon className="h-5 w-5" />
              </div>
            )}
            <Input
              id="email"
              placeholder="Email"
              value={form.getField("email")}
              onChange={(e: any) => {
                const input = e.target;
                const start = input?.selectionStart;
                const end = input?.selectionEnd;

                const lower = input.value?.toLowerCase();
                form.setField("email", lower);
                requestAnimationFrame(() => {
                  input.setSelectionRange(start, end);
                });
                if (emailRegEx.test(form.getField("email"))) {
                  setIsValidEmail(true);
                } else {
                  setIsValidEmail(false);
                }
              }}
              disabled={isEmailVerified}
              className={inputFieldStyles}
            />
            <label htmlFor="email" className={floatingLabelStyles}>
              Email*
            </label>
            {!isEmailVerified && (
              <div className="absolute top-1/2 right-3 z-10 -translate-y-1/2">
                <Button
                  className="h-8 px-3 text-xs"
                  disabled={!isValidEmail}
                  onClick={async () => {
                    await handleSubmitVerify("email");
                  }}
                >
                  Verify
                </Button>
              </div>
            )}
            {form.errors.get("email") && (
              <p className={errorStyles}>{form.errors.get("email")[0]}</p>
            )}
          </div>
          <div className="w-full">
            <div className="flex gap-1 items-center">
              <div className={`relative w-full`}>
                {isMobileVerified && (
                  <div className="absolute top-1/2 right-3 z-10 -translate-y-1/2">
                    <CheckIcon className="h-5 w-5" />
                  </div>
                )}
                <Input
                  id="mobile"
                  type="number"
                  placeholder={
                    form.getField("type")?.value === "proprietors"
                      ? "Mobile (Proprietor)*"
                      : "Mobile*"
                  }
                  value={form.getField("mobile")}
                  disabled={isMobileVerified}
                  onChange={(e: any) => {
                    if (e.target.value >= 0 || e.target.value == "") {
                      form.setField("mobile", e?.target?.value);
                      if (mobileNumberRegEx.test(form.getField("mobile"))) {
                        setIsValidMobile(true);
                      } else {
                        setIsValidMobile(false);
                      }
                    }
                  }}
                  className={inputFieldStyles}
                />
                <label htmlFor="mobile" className={floatingLabelStyles}>
                  {form.getField("type")?.value === "proprietors"
                    ? "Mobile (Proprietor)*"
                    : "Mobile*"}
                </label>
                {!isMobileVerified && (
                  <div className="absolute top-1/2 right-3 z-10 -translate-y-1/2">
                    <Button
                      className="h-8 px-3 text-xs"
                      disabled={!isValidMobile}
                      onClick={async () => {
                        await handleSubmitVerify("mobile");
                      }}
                    >
                      Verify
                    </Button>
                  </div>
                )}
              </div>
              {isMobileVerified &&
                isEmailVerified &&
                process.env.NEXT_PUBLIC_ENABLE_PROFILE_EDIT === "true" && (
                  <div
                    className="mr-2 md:mr-0 cursor-pointer"
                    onClick={() => {
                      setMobileChangeState({
                        isConfirmingOpen: true,
                        isConfirmingChange: false,
                      });
                    }}
                  >
                    <EditIcon className="h-6 w-6" />
                  </div>
                )}
            </div>
            {form.errors.get("mobile") && (
              <p className={errorStyles}>{form.errors.get("mobile")[0]}</p>
            )}
          </div>

          <FloatingLabelInput
            id="alternate_mobile"
            type="number"
            placeholder="Enter alternate mobile number"
            disabled={userOnboarding?.alternate_mobile}
            value={form.getField("alternate_mobile")}
            onChange={(e) => {
              if (
                (parseInt(e?.target?.value) >= 0 &&
                  e?.target?.value?.length < 11) ||
                e?.target?.value === ""
              ) {
                form.setField("alternate_mobile", e?.target?.value);
              }
            }}
            label="Alternate Mobile"
            error={form.errors.get("alternate_mobile")?.[0]}
            inputFieldStyles={inputFieldStyles}
            floatingLabelStyles={floatingLabelStyles}
            errorStyles={errorStyles}
          />
          <div className="">
            {type ? (
              type === "individual" ? (
                <FloatingLabelInput
                  id="aadhaar"
                  type="number"
                  placeholder="Enter Aadhaar number"
                  disabled={!userCanUpdate}
                  value={form.getField("aadhaar")}
                  onChange={(e) => {
                    const value = e?.target?.value;

                    if (
                      (parseInt(value) >= 0 && value?.length < 13) ||
                      value === ""
                    ) {
                      form.setField("aadhaar", value);
                    }
                  }}
                  label="Aadhaar number*"
                  error={form.errors.get("aadhaar")?.[0]}
                  inputFieldStyles={inputFieldStyles}
                  floatingLabelStyles={floatingLabelStyles}
                  errorStyles={errorStyles}
                />
              ) : (
                <FloatingLabelInput
                  id="gst"
                  placeholder="Enter GST number"
                  disabled={!userCanUpdate}
                  value={form.getField("gst")}
                  onChange={(e) => {
                    const value = e?.target?.value;

                    if (
                      (parseInt(value) >= 0 && value?.length < 16) ||
                      value === ""
                    ) {
                      form.setField("gst", value?.toUpperCase());
                    }
                  }}
                  label="GST Number*"
                  error={form.errors.get("gst")?.[0]}
                  inputFieldStyles={inputFieldStyles}
                  floatingLabelStyles={floatingLabelStyles}
                  errorStyles={errorStyles}
                />
              )
            ) : null}
          </div>
        </FormSection>

        <FormSection id="onboarding-location-details" title="Location Details">
          <FloatingLabelInput
            id="pin"
            type="number"
            placeholder="Enter pin code"
            disabled={!userCanUpdate}
            value={form.getField("pin")}
            onChange={(e: any) => {
              if (parseInt(e?.target?.value) >= 0 || e?.target?.value === "") {
                handlePinCodeChange(e?.target?.value);
              }
            }}
            label="Pin code*"
            error={form.errors.get("pin")?.[0]}
            inputFieldStyles={inputFieldStyles}
            floatingLabelStyles={floatingLabelStyles}
            errorStyles={errorStyles}
          />
          <FloatingLabelInput
            id="city"
            placeholder="Enter city"
            disabled={isPincodeBasedFetchingEnabled ? true : !userCanUpdate}
            value={form.getField("city")}
            onChange={(e) => form.setField("city", e?.target?.value)}
            label="City*"
            error={form.errors.get("city")?.[0]}
            inputFieldStyles={inputFieldStyles}
            floatingLabelStyles={floatingLabelStyles}
            errorStyles={errorStyles}
          />
          <FloatingLabelInput
            id="state"
            placeholder="Enter state"
            disabled={isPincodeBasedFetchingEnabled ? true : !userCanUpdate}
            value={form.getField("state")}
            onChange={(e) => form.setField("state", e?.target?.value)}
            label="State*"
            error={form.errors.get("state")?.[0]}
            inputFieldStyles={inputFieldStyles}
            floatingLabelStyles={floatingLabelStyles}
            errorStyles={errorStyles}
          />
          <FloatingLabelInput
            id="country"
            placeholder="Enter country"
            disabled={isPincodeBasedFetchingEnabled ? true : !userCanUpdate}
            value={form.getField("country")}
            onChange={(e: any) => form.setField("country", e.target.value)}
            label="Country*"
            error={form.errors.get("country")?.[0]}
            inputFieldStyles={inputFieldStyles}
            floatingLabelStyles={floatingLabelStyles}
            errorStyles={errorStyles}
          />
          <FloatingLabelInput
            id="full_address"
            placeholder="Enter full address"
            disabled={!userCanUpdate}
            value={form.getField("full_address")}
            onChange={(e) => form.setField("full_address", e?.target?.value)}
            label="Full address*"
            error={form.errors.get("full_address")?.[0]}
            inputFieldStyles={inputFieldStyles}
            floatingLabelStyles={floatingLabelStyles}
            errorStyles={errorStyles}
          />
        </FormSection>

        <FormSection
          id="onboarding-preference-details"
          title="Preference Details"
        >
          <div className="w-full" id="preferred_property_type">
            <ScrollableDropdown
              // disabled={!userCanUpdate}
              dataSource={getAssetsDataSource}
              defaultSelected={form.getField("preferred_property_type")}
              onChange={(value: any[]) => {
                form.setField("preferred_property_type", value);
              }}
              valueKey="value"
              labelKey="label"
              placeholder="Select property type"
              multiple={true}
              label="Preferred property type"
              error={
                form.errors.get("preferred_property_type") && (
                  <p className={errorStyles}>
                    {form.errors.get("preferred_property_type")[0]}
                  </p>
                )
              }
              styles={{
                hideDot: true,
                dropdownItemsClass: "text-brand-borderGrey",
                hoverBgColor: "focus:bg-brand-lightPink",
                hoverTextColor: "focus:text-brand-borderGrey",
                outerContainer: `${dropdownContainerStyles} ${
                  form.getField("preferred_property_type")?.length > 0
                    ? "items-end text-brand-textLightGrey"
                    : "text-brand-borderGrey"
                }`,
              }}
            />
          </div>
          <div className="w-full relative" id="price_range">
            <Dropdown
              // disabled={!userCanUpdate}
              dataSource={() => {
                return Promise.resolve(priceRange);
              }}
              defaultSelected={
                form.getField("price_range") !== ""
                  ? form.getField("price_range")
                  : undefined
              }
              selectIdentifier={true}
              onChange={(value: any) => form.setField("price_range", value)}
              valueKey="value"
              placeholder="Select price range"
              styles={{
                hideDot: true,
                dropdownItemsClass: "text-brand-borderGrey",
                hoverBgColor: "focus:bg-brand-lightPink",
                hoverTextColor: "focus:text-brand-borderGrey",
                outerContainer: `${dropdownContainerStyles} ${
                  // !userCanUpdate
                  //   ? "bg-gray-50 cursor-not-allowed"
                  //   :
                  "bg-white cursor-pointer"
                } ${
                  form.getField("price_range")?.value
                    ? !userCanUpdate
                      ? "items-end text-gray-600"
                      : "items-end text-brand-textLightGrey"
                    : !userCanUpdate
                    ? "text-gray-500"
                    : "text-brand-borderGrey"
                } ${!form.getField("price_range")?.value ? "pt-3" : ""}`,
              }}
            />
            <div
              className={`${floatingLabelDropdownStyles} ${
                form.getField("price_range")?.value
                  ? !userCanUpdate
                    ? "text-gray-500"
                    : "text-brand-borderGrey"
                  : !userCanUpdate
                  ? "text-gray-400"
                  : "text-gray-400"
              }`}
            >
              Price range
            </div>
            {form.errors.get("price_range") && (
              <p className={errorStyles}>{form.errors.get("price_range")[0]}</p>
            )}
          </div>

          <div className="w-full" id="preferred_state">
            <ScrollableDropdown
              // disabled={!userCanUpdate}
              dataSource={getStatesDataSource}
              defaultSelected={form.getField("preferred_state")}
              onChange={(value: any[]) => {
                form.setField("preferred_state", value);
              }}
              valueKey="value"
              labelKey="label"
              placeholder="Select state"
              multiple={true}
              label="Preferred State"
              error={
                form.errors.get("preferred_state") && (
                  <p className={errorStyles}>
                    {form.errors.get("preferred_state")[0]}
                  </p>
                )
              }
              styles={{
                hideDot: true,
                dropdownItemsClass: "text-brand-borderGrey",
                hoverBgColor: "focus:bg-brand-lightPink",
                hoverTextColor: "focus:text-brand-borderGrey",
                outerContainer: `${dropdownContainerStyles} ${
                  form.getField("preferred_state")?.length > 0
                    ? "items-end text-brand-textLightGrey"
                    : "text-brand-borderGrey"
                }`,
              }}
            />
          </div>
          <div className="w-full" id="preferred_location">
            <ScrollableDropdown
              // disabled={!userCanUpdate}
              dataSource={getCitiesDataSource}
              defaultSelected={form.getField("preferred_location")}
              onChange={(value: any[]) => {
                form.setField("preferred_location", value);
              }}
              valueKey="value"
              labelKey="label"
              placeholder="Select city"
              multiple={true}
              label="Preferred City"
              error={
                form.errors.get("preferred_location") && (
                  <p className={errorStyles}>
                    {form.errors.get("preferred_location")[0]}
                  </p>
                )
              }
              styles={{
                hideDot: true,
                dropdownItemsClass: "text-brand-borderGrey",
                hoverBgColor: "focus:bg-brand-lightPink",
                hoverTextColor: "focus:text-brand-borderGrey",
                outerContainer: `${dropdownContainerStyles} ${
                  form.getField("preferred_location")?.length > 0
                    ? "items-end text-brand-textLightGrey"
                    : "text-brand-borderGrey"
                }`,
              }}
            />
          </div>
        </FormSection>
      </section>
      <div className="flex justify-center">
        <Button
          disabled={loading}
          className="w-full max-w-[514px] h-12 text-[22px] mb-6"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </div>
      <ConfirmActionModal
        mobileChangeState={mobileChangeState}
        setMobileChangeState={setMobileChangeState}
      />
      <BidderInputMobileNumberFormModel
        currentMobileNumber={form.getField("mobile")}
        mobileChangeState={mobileChangeState}
        setMobileChangeState={setMobileChangeState}
      />
      <BidderOTPInputModal
        isEmail={isEmail}
        inputModelOpen={inputModelOpen}
        setInputModalOpen={setInputModalOpen}
        userIdentity={
          isEmail ? form.getField("email") : form.getField("mobile")
        }
      />
      {isFetchingPan && <LoadingModal />}
    </>
  );
}

interface LoadingModalProps {
  loadingText?: string;
}

const LoadingModal = ({ loadingText }: LoadingModalProps) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className=" text-white flex flex-col relative">
        <div className="flex flex-col flex-grow h-full">
          <div className="flex justify-center items-center">
            <div className="w-12 h-12 border-8 border-t-6 border-brand-orange rounded-full animate-spin bg-gradient-to-r from-brand-orange to-blue-100 shadow-lg"></div>
          </div>
          <div className="mt-2 text-sm font-openSans text-center">
            {loadingText || "Validating PAN. Please wait"}
          </div>
        </div>
      </div>
    </div>
  );
};

interface ConfirmActionModal {
  setMobileChangeState: any;
  mobileChangeState: any;
}
const ConfirmActionModal = ({
  mobileChangeState,
  setMobileChangeState,
}: ConfirmActionModal) => {
  return (
    <CustomModalLayout
      open={mobileChangeState?.isConfirmingOpen}
      childrenClassName="pb-6 px-8 overflow-y-scroll"
      className="pt-8 lg:pb-[30px] mx-4 overflow-y-scroll max-h-[calc(100%-10px)]"
      onClose={() =>
        setMobileChangeState({
          isConfirmingChange: false,
          isConfirmingOpen: false,
        })
      }
    >
      <div className="font-openSans">
        <h4 className="text-base font-semibold mb-2">
          Are you sure you want to change your existing mobile number?
        </h4>

        <div className="space-y-2 text-xs text-brand-textGrey">
          <p>
            Changing your mobile number is not recommended as it will not affect
            your previous auction records.
          </p>
          <p>
            Please note that updating your mobile number will not impact ongoing
            auctions you are already participating in, but it may lead to
            confusion in tracking or historical data.
          </p>
          <p>
            If you are currently participating in any active auctions, we
            recommend waiting until those auctions have ended before changing
            your mobile number.
          </p>
        </div>
      </div>
      <div className="h-full flex flex-col flex-grow space-y-8 mt-16">
        <Button
          className="w-full"
          onClick={() =>
            setMobileChangeState({
              isConfirmingOpen: false,
              isConfirmingChange: true,
            })
          }
        >
          Confirm
        </Button>
        <Button
          variant="ghost"
          className="w-full mb-[60px] bg-brand-bgGrey"
          onClick={() =>
            setMobileChangeState({
              isConfirmingChange: false,
              isConfirmingOpen: false,
            })
          }
        >
          Cancel
        </Button>
      </div>
    </CustomModalLayout>
  );
};

const FloatingLabelInput = ({
  id,
  placeholder,
  value,
  onChange,
  disabled = false,
  type = "text",
  label,
  error,
  showCheckIcon = false,
  rightElement = null,
  inputFieldStyles,
  floatingLabelStyles,
  errorStyles,
}: {
  id: string;
  placeholder: string;
  value: string;
  onChange: (e: any) => void;
  disabled?: boolean;
  type?: string;
  label: string;
  error?: string;
  showCheckIcon?: boolean;
  rightElement?: React.ReactNode;
  inputFieldStyles: string;
  floatingLabelStyles: string;
  errorStyles: string;
}) => {
  return (
    <div className="relative w-full">
      {showCheckIcon && (
        <div className="absolute top-1/2 right-3 z-10 -translate-y-1/2">
          <CheckIcon className="h-5 w-5" />
        </div>
      )}
      <Input
        id={id}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        onChange={onChange}
        className={inputFieldStyles}
      />
      <label htmlFor={id} className={floatingLabelStyles}>
        {label}
      </label>
      {rightElement}
      {error && <p className={errorStyles}>{error}</p>}
    </div>
  );
};

export type FormSectionProps = {
  id?: string;
  title: string;
  children: ReactNode;
  className?: string;
};

const FormSection = ({
  id = "",
  title,
  children,
  className = "",
}: FormSectionProps) => {
  const sectionProps =
    typeof id === "string" && id.trim().length > 0 ? { id } : {};

  return (
    <section
      {...sectionProps}
      className={`border p-4 grid grid-cols-1 md:grid-cols-2 gap-y-5 gap-x-4 items-start ${className}`}
    >
      <h2 className="font-semibold col-span-full">{title}</h2>
      {children}
    </section>
  );
};
